import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MapPin,
  Instagram,
  Wifi,
  Music2,
  Umbrella,
  Bath,
  DoorOpen,
  Wine,
  QrCode,
  Sparkles,
  Trees,
  Sun,
  ChevronDown,
  HandHeart,
  Martini,
} from "lucide-react";

const VIDEO_URL = "/media/video3Sorriso.mp4";

const MAPS_URL =
  "https://www.google.com/maps/place/Plaja+Sorriso/@44.3054308,26.0198144,8z/data=!4m20!1m13!4m12!1m4!2m2!1d26.0440064!2d44.433408!4e1!1m6!1m2!1s0x40baf162eac6ff6f:0xef4bb4fd9a0ef07f!2sPlaja+Sorriso,+6JJJ%2B2G,+Constan%C8%9Ba!2m2!1d28.6312566!2d44.2300658!3m5!1s0x40baf162eac6ff6f:0xef4bb4fd9a0ef07f!8m2!3d44.2300658!4d28.6312566!16s%2Fg%2F11svytgg2y?entry=ttu";

const INSTAGRAM_URL = "https://www.instagram.com/sorrisobeach/";

const I18N = {
  ro: {
    locale: "ro",
    eyebrow: "Beach · Bar · Vibes",
    subtitle: "Plaja ta de zâmbet în Mamaia — vis-à-vis de Hotel Ovidiu.",
    locationKicker: "Locație",
    menuKicker: "Meniu 2026",
    openMenu: "Vezi meniul",
    backToSite: "Înapoi pe site",
    openInNewTab: "Deschide în tab nou",
    openInMaps: "Deschide în Maps",
    openInGoogleMaps: "Deschide în Google Maps",
    scrollAria: "Vezi facilități",
    facilitiesEyebrow: "Ce găsești aici",
    facilitiesTitleA: "Facilități la ",
    facilitiesTitleB: "Sorriso",
    instagramCta: "@sorrisobeach pe Instagram",
    addressKicker: "Cum ajungi",
    addressBody: (
      <>
        Plaja Sorriso se află în <strong>Mamaia</strong>, vis-à-vis de{" "}
        <strong>Hotel Ovidiu</strong>. Te așteptăm cu un zâmbet și cu cea mai
        bună vedere la mare.
      </>
    ),
    footerLocation: "Mamaia · România",
    footerRights: (year) =>
      `© ${year} Sorriso. Toate drepturile rezervate.`,
    facilities: [
      { icon: Wine, title: "Beach Bar", desc: "Terasă mare, prețuri corecte" },
      { icon: Sparkles, title: "Toaletă curată", desc: "Întreținută zilnic" },
      {
        icon: Umbrella,
        title: "Umbrele & saltele",
        desc: "Saltele noi, umbrele mari și umbrele de stuf",
      },
      { icon: Music2, title: "Muzică bună", desc: "Atmosferă pe cinste" },
      { icon: Wifi, title: "Wi-Fi gratuit", desc: "Stai conectat, stai relaxat" },
      { icon: Bath, title: "Duș", desc: "Fără nisip pe drum spre casă" },
      { icon: DoorOpen, title: "Cabină de schimb", desc: "Confort & intimitate" },
      { icon: HandHeart, title: "Zonă de masaj", desc: "Servicii de masaj — contra cost" },
      { icon: QrCode, title: "Comandă cu QR", desc: "Direct de la şezlong" },
      { icon: Sun, title: "Vedere completă", desc: "Mare deschisă, răsărit" },
    ],
  },
  en: {
    locale: "en",
    eyebrow: "Beach · Bar · Vibes",
    subtitle: "Your smile beach in Mamaia — across from Hotel Ovidiu.",
    locationKicker: "Location",
    menuKicker: "Menu 2026",
    openMenu: "View menu",
    backToSite: "Back to site",
    openInNewTab: "Open in new tab",
    openInMaps: "Open in Maps",
    openInGoogleMaps: "Open in Google Maps",
    scrollAria: "See facilities",
    facilitiesEyebrow: "What you'll find",
    facilitiesTitleA: "Facilities at ",
    facilitiesTitleB: "Sorriso",
    instagramCta: "@sorrisobeach on Instagram",
    addressKicker: "How to get here",
    addressBody: (
      <>
        Sorriso Beach is in <strong>Mamaia</strong>, right across from{" "}
        <strong>Hotel Ovidiu</strong>. We welcome you with a smile and the best
        sea view in town.
      </>
    ),
    footerLocation: "Mamaia · Romania",
    footerRights: (year) => `© ${year} Sorriso. All rights reserved.`,
    facilities: [
      { icon: Wine, title: "Beach Bar", desc: "Large terrace, fair prices" },
      { icon: Sparkles, title: "Clean toilets", desc: "Maintained daily" },
      {
        icon: Umbrella,
        title: "Sunbeds & umbrellas",
        desc: "New mattresses, large umbrellas & straw parasols",
      },
      { icon: Music2, title: "Good music", desc: "Coastal atmosphere" },
      { icon: Wifi, title: "Free Wi-Fi", desc: "Stay connected, stay relaxed" },
      { icon: Bath, title: "Shower", desc: "Leave the sand behind" },
      { icon: DoorOpen, title: "Changing cabin", desc: "Comfort & privacy" },
      { icon: HandHeart, title: "Massage zone", desc: "Massage services — extra charge" },
      { icon: QrCode, title: "QR ordering", desc: "Order from your sunbed" },
      { icon: Sun, title: "Full sea view", desc: "Open horizon, dreamy sunrise" },
    ],
  },
};

const STORAGE_KEY = "sorriso_lang";

export default function Sorriso() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "ro";
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === "ro" || saved === "en") return saved;
    const nav = (navigator.language || "ro").toLowerCase();
    return nav.startsWith("ro") ? "ro" : "en";
  });

  const t = I18N[lang];

  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.setAttribute("muted", "");
      v.setAttribute("playsinline", "");
      v.setAttribute("webkit-playsinline", "");
      v.muted = true;
      v.playsInline = true;
      const tryPlay = () => v.play().catch(() => {});
      tryPlay();
    }
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, lang);
      document.documentElement.setAttribute("lang", lang);
    }
  }, [lang]);

  const scrollToFacilities = () => {
    document
      .getElementById("facilitati")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const toggleLang = () => setLang((l) => (l === "ro" ? "en" : "ro"));

  // When arriving via QR code (sorrisobeach.ro/?meniu) open the menu choice
  // page so beach guests can pick drinks (bar menu) or food (Foodstation).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("meniu") || window.location.hash === "#meniu") {
      navigate("/meniu", { replace: true });
    }
  }, [navigate]);

  return (
    <div
      className="min-h-screen w-full bg-[#f6f1e7] text-[#1a1a1a]"
      data-testid="sorriso-page"
      style={{ fontFamily: "'TeX Gyre Adventor', 'Tex Gyre Adventor', sans-serif" }}
    >
      {/* HERO – full viewport video */}
      <section
        className="relative h-[100svh] w-full overflow-hidden bg-black"
        data-testid="hero-section"
      >
        <video
          ref={videoRef}
          src={VIDEO_URL}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 h-full w-full object-cover"
          data-testid="hero-video"
        />

        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/70" />

        {/* Top bar */}
        <div
          className={`fixed left-0 right-0 top-0 z-30 flex items-center justify-between px-5 py-4 transition-all duration-500 ${
            scrolled ? "bg-[#f6f1e7]/90 backdrop-blur-md" : "bg-transparent"
          }`}
          data-testid="top-bar"
        >
          <div
            className={`flex items-center gap-2 transition-colors ${
              scrolled ? "text-[#1a1a1a]" : "text-white"
            }`}
          >
            <span
              className="text-[22px] leading-none tracking-tight"
              style={{
                fontFamily: "'TeX Gyre Adventor', 'Tex Gyre Adventor', sans-serif",
              }}
            >
              Sorriso Beach
            </span>
            <span className="h-1 w-1 rounded-full bg-[#e8a13a]" />
            <span className="text-[11px] uppercase tracking-[0.22em] opacity-80">
              Mamaia
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* Language toggle */}
            <button
              type="button"
              onClick={toggleLang}
              data-testid="lang-toggle"
              aria-label={lang === "ro" ? "Switch to English" : "Comută în română"}
              className={`relative flex h-9 items-center rounded-full border px-1 text-[11px] font-semibold uppercase tracking-[0.18em] transition-all ${
                scrolled
                  ? "border-[#1a1a1a]/15 text-[#1a1a1a]"
                  : "border-white/40 text-white"
              }`}
            >
              <span
                className={`relative z-10 px-2 py-1 transition-colors ${
                  lang === "ro"
                    ? scrolled
                      ? "text-[#f6f1e7]"
                      : "text-black"
                    : ""
                }`}
                data-testid="lang-ro"
              >
                RO
              </span>
              <span
                className={`relative z-10 px-2 py-1 transition-colors ${
                  lang === "en"
                    ? scrolled
                      ? "text-[#f6f1e7]"
                      : "text-black"
                    : ""
                }`}
                data-testid="lang-en"
              >
                EN
              </span>
              <span
                aria-hidden="true"
                className={`absolute top-1 bottom-1 w-9 rounded-full transition-all duration-300 ${
                  scrolled ? "bg-[#1a1a1a]" : "bg-white"
                } ${lang === "ro" ? "left-1" : "left-[40px]"}`}
              />
            </button>

            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              data-testid="instagram-top-link"
              className={`flex h-9 w-9 items-center justify-center rounded-full border transition-all ${
                scrolled
                  ? "border-[#1a1a1a]/15 text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-[#f6f1e7]"
                  : "border-white/40 text-white hover:bg-white hover:text-black"
              }`}
            >
              <Instagram className="h-4 w-4" strokeWidth={1.6} />
            </a>
          </div>
        </div>



        {/* Floating action buttons */}
        <div className="absolute bottom-6 left-0 right-0 z-20 flex flex-col items-center gap-3 px-6">
          {/* Menu button – primary (glass). Opens the menu choice page (drinks / food). */}
          <Link
            to="/meniu"
            data-testid="menu-button"
            className="group flex w-full max-w-md items-center justify-between rounded-full border border-white/30 bg-white/15 px-5 py-4 text-white shadow-[0_10px_40px_-10px_rgba(0,0,0,0.55)] backdrop-blur-md transition-all hover:bg-white/25 active:scale-[0.98]"
          >
            <span className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-white ring-1 ring-white/40">
                <Martini className="h-4 w-4" strokeWidth={2} />
              </span>
              <span className="flex flex-col items-start leading-tight">
                <span className="text-[10px] uppercase tracking-[0.25em] text-white/70">
                  {t.menuKicker}
                </span>
                <span className="text-[15px] font-semibold">
                  {t.openMenu}
                </span>
              </span>
            </span>
            <span className="text-[11px] tracking-wide text-white/70 group-hover:text-white">
              →
            </span>
          </Link>

          {/* Maps button */}
          <a
            href={MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="maps-button"
            className="group flex w-full max-w-md items-center justify-between rounded-full bg-[#1a1a1a] px-5 py-4 text-[#f6f1e7] shadow-[0_10px_40px_-10px_rgba(0,0,0,0.6)] ring-1 ring-white/10 transition-all active:scale-[0.98]"
          >
            <span className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#e8a13a] text-black">
                <MapPin className="h-4 w-4" strokeWidth={2} />
              </span>
              <span className="flex flex-col items-start leading-tight">
                <span className="text-[10px] uppercase tracking-[0.25em] text-white/60">
                  {t.locationKicker}
                </span>
                <span className="text-[15px] font-semibold">
                  {t.openInMaps}
                </span>
              </span>
            </span>
            <span className="text-[11px] tracking-wide text-white/70 group-hover:text-white">
              →
            </span>
          </a>
        </div>

        {/* Scroll cue */}
        <button
          onClick={scrollToFacilities}
          aria-label={t.scrollAria}
          data-testid="scroll-cue"
          className="absolute bottom-[210px] left-1/2 z-20 hidden -translate-x-1/2 sm:block"
        >
          <ChevronDown className="h-5 w-5 animate-bounce text-white/70" />
        </button>
      </section>

      {/* FACILITIES */}
      <section
        id="facilitati"
        className="relative px-5 pb-20 pt-16"
        data-testid="facilities-section"
      >
        <div className="mx-auto w-full max-w-md">
          <div className="mb-10">
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#1a1a1a]/55">
              {t.facilitiesEyebrow}
            </p>
            <h2
              className="mt-2 text-[#1a1a1a]"
              style={{
                fontFamily: "'TeX Gyre Adventor', 'Tex Gyre Adventor', sans-serif",
                fontSize: "clamp(34px, 9vw, 48px)",
                lineHeight: 1,
                letterSpacing: "-0.02em",
                fontWeight: 700,
              }}
              data-testid="facilities-title"
            >
              {t.facilitiesTitleA}<span className="italic text-[#e8a13a]">{t.facilitiesTitleB}</span>
            </h2>
            <div className="mt-5 h-px w-12 bg-[#1a1a1a]/30" />
          </div>

          <ul className="flex flex-col" data-testid="facilities-list">
            {t.facilities.map((f, i) => {
              const Icon = f.icon;
              return (
                <li
                  key={`${lang}-${f.title}`}
                  data-testid={`facility-${i}`}
                  className="group flex items-center gap-4 border-b border-[#1a1a1a]/10 py-5 last:border-b-0"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#1a1a1a] text-[#f6f1e7] transition-transform group-hover:scale-105">
                    <Icon className="h-[18px] w-[18px]" strokeWidth={1.7} />
                  </span>
                  <div className="flex flex-1 flex-col">
                    <span className="text-[15px] font-semibold leading-tight text-[#1a1a1a]">
                      {f.title}
                    </span>
                    <span className="text-[12.5px] leading-snug text-[#1a1a1a]/60">
                      {f.desc}
                    </span>
                  </div>
                  <span className="text-[10px] tracking-[0.2em] text-[#1a1a1a]/30">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </li>
              );
            })}
          </ul>

          {/* Secondary CTAs */}
          <div className="mt-10 flex flex-col gap-3">
            <Link
              to="/meniu"
              data-testid="menu-button-bottom"
              className="flex items-center justify-center gap-2 rounded-full border border-[#1a1a1a]/15 bg-[#1a1a1a]/5 px-5 py-4 text-[14px] font-semibold text-[#1a1a1a] backdrop-blur-md transition-colors hover:bg-[#1a1a1a] hover:text-[#f6f1e7] active:scale-[0.98]"
            >
              <Martini className="h-4 w-4" strokeWidth={2} />
              {t.openMenu}
            </Link>
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="maps-button-bottom"
              className="flex items-center justify-center gap-2 rounded-full bg-[#1a1a1a] px-5 py-4 text-[14px] font-semibold text-[#f6f1e7] active:scale-[0.98]"
            >
              <MapPin className="h-4 w-4" strokeWidth={2} />
              {t.openInGoogleMaps}
            </a>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-button"
              className="flex items-center justify-center gap-2 rounded-full border border-[#1a1a1a]/15 bg-transparent px-5 py-4 text-[14px] font-semibold text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-[#f6f1e7] transition-colors"
            >
              <Instagram className="h-4 w-4" strokeWidth={1.8} />
              {t.instagramCta}
            </a>
          </div>

          {/* Footer */}
          <footer
            className="mt-14 flex flex-col items-center gap-2 text-center text-[11px] text-[#1a1a1a]/50"
            data-testid="footer"
          >
            <span
              style={{
                fontFamily: "'TeX Gyre Adventor', 'Tex Gyre Adventor', sans-serif",
              }}
              className="text-[18px] text-[#1a1a1a]"
            >
              Sorriso Beach
            </span>
            <span className="tracking-[0.2em] uppercase">
              {t.footerLocation}
            </span>
            <span className="mt-3 opacity-70">
              {t.footerRights(new Date().getFullYear())}
            </span>
          </footer>
        </div>
      </section>
    </div>
  );
}
